import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  EyeIcon, 
  EyeSlashIcon, 
  LockClosedIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  PhoneIcon,
  UserIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import { SignupCredentials, SignupData, RestaurantDetails } from '../types';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { buildRestaurantFrontendOrigin } from '../utils/restaurantUtils';
import { useToast } from '../context/ToastContext';

interface SignupProps {
  onBack: () => void;
}

const Signup: React.FC<SignupProps> = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [userName, setUserName] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [hasAcceptedPolicies, setHasAcceptedPolicies] = useState(false);
  const [policyError, setPolicyError] = useState('');
  const { signup } = useAuth();
  const { showError, showSuccess } = useToast();

  const renderPolicyAgreement = () => (
    <div className="pt-2">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={hasAcceptedPolicies}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setHasAcceptedPolicies(event.target.checked);
            if (event.target.checked) {
              setPolicyError('');
              setError('');
            }
          }}
          className="mt-1 h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-pink-600"
        />
        <span className="text-sm text-gray-600 leading-relaxed text-left">
          I have read and agree to the
          {' '}<a href="https://menunix.com/terms" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Menunix Terms</a>,
          {' '}<a href="https://menunix.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Privacy Policy</a>,
          {' '}<a href="https://menunix.com/cancellation-refund" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Cancellation &amp; Refund</a>,
          {' '}and <a href="https://menunix.com/shipping-exchange" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline">Shipping &amp; Exchange</a> policies.
        </span>
      </label>
      {policyError && (
        <p className="mt-2 text-sm text-red-600">{policyError}</p>
      )}
    </div>
  );

  // Empty restaurant data - will be filled by user
  const defaultRestaurantData: RestaurantDetails = {
    id: "",
    name: "",
    description: "",
    cuisine: "",
    phone: "",
    email: "",
    address: ""
  };

  const {
    register: registerStep1,
    watch: watchStep1,
    formState: { errors: errorsStep1 },
    trigger: triggerStep1,
  } = useForm<SignupCredentials>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    }
  });

  const {
    register: registerStep2,
    handleSubmit: handleSubmitStep2,
    setValue,
    watch: watchStep2,
    formState: { errors: errorsStep2 },
  } = useForm<RestaurantDetails>({
    defaultValues: defaultRestaurantData
  });
  const shopUsername = watchStep2('id');

  const shopPreviewUrl = useMemo(() => {
    if (!shopUsername || !shopUsername.trim()) {
      return '';
    }
    try {
      return buildRestaurantFrontendOrigin({ id: shopUsername.trim() });
    } catch (error) {
      console.warn('Failed to build shop preview URL', error);
      return '';
    }
  }, [shopUsername]);

  const shopUsernameField = registerStep2('id', { 
    required: 'Shop Username is required',
    pattern: {
      value: /^(?!-)(?!.*-$)(?![0-9]+$)[a-z0-9-]{1,63}$/,
      message:
        'Shop username can contain letters (a–z), numbers (0–9), and hyphens. It cannot start or end with a hyphen, cannot be only numbers, and must be at most 63 characters.'
    },
    setValueAs: (value: string) => value?.toLowerCase().trim()
  });

  // Remove password watching since it's not part of RestaurantDetails

  // Initialize form with default values
  React.useEffect(() => {
    Object.entries(defaultRestaurantData).forEach(([key, value]) => {
      setValue(key as keyof RestaurantDetails, value);
    });
  }, [setValue]);

  // Set phone number when mobile is verified
  React.useEffect(() => {
    if (mobileVerified && mobileNumber) {
      setValue('phone', `+91 ${mobileNumber}`);
    }
  }, [mobileVerified, mobileNumber, setValue]);

  const sendOtp = async () => {
    if (!hasAcceptedPolicies) {
      const message = 'Please review and accept the Menunix policies before requesting an OTP.';
      setPolicyError(message);
      setError(message);
      return;
    }

    if (!mobileNumber || mobileNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    // Additional validation for Indian mobile numbers
    if (!/^[6-9]\d{9}$/.test(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number starting with 6-9');
      return;
    }

    if (!userName || userName.trim().length < 2) {
      setError('Please enter your full name');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await apiService.sendOtp(mobileNumber);
      if (result.status) {
        setIsOtpSent(true);
        showSuccess('OTP sent', `OTP sent to +91 ${mobileNumber}`);
      } else {
        showError('Failed to send OTP', result.message || 'Please try again.');
      }
    } catch (err) {
      showError('Failed to send OTP', 'Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setError('');

    try {
      const result = await apiService.verifyOtp(mobileNumber, otp);
      if (result.status) {
        setMobileVerified(true);
        setCurrentStep(2);
        showSuccess('OTP verified', 'Mobile number verified successfully');
      } else {
        showError('Invalid OTP', result.message || 'Please try again.');
      }
    } catch (err) {
      showError('Failed to verify OTP', 'Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const onSubmitStep2 = async (restaurantData: RestaurantDetails) => {
    if (!hasAcceptedPolicies) {
      const message = 'Please review and accept the Menunix policies before creating your account.';
      setPolicyError(message);
      setError(message);
      showError('Accept policies', message);
      return;
    }

    setPolicyError('');
    setIsLoading(true);
    setError('');

    try {
      localStorage.setItem('selectedTab', 'profile');
      // First validate step 1 form (password confirmation)
      const step1Form = document.querySelector('form') as HTMLFormElement;
      const step1FormData = new FormData(step1Form);
      const password = step1FormData.get('password') as string;
      const confirmPassword = step1FormData.get('confirmPassword') as string;
      
      // Validate user name (from state)
      if (!userName || userName.trim().length < 2) {
        setError('Please enter your full name');
        setIsLoading(false);
        return;
      }
      
      // Validate password presence
      if (!password) {
        setError('Password is required');
        showError('Password required', 'Please enter your password');
        setIsLoading(false);
        return;
      }

      // Validate password confirmation
      if (!confirmPassword) {
        setError('Please confirm your password');
        showError('Confirmation required', 'Please confirm your password');
        setIsLoading(false);
        return;
      }
      
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        showError('Passwords do not match', 'Please ensure both passwords are identical');
        setIsLoading(false);
        return;
      }
      
      // Format FSSAI number with prefix
      const fssaiNumber = restaurantData.description?.trim() || '';
      const formattedFssai = fssaiNumber ? `FSSAI License No. ${fssaiNumber}` : '';
      
      const signupData: SignupData = {
        password: password,
        name: restaurantData.name, // Shop name
        id: restaurantData.id,
        address: restaurantData.address,
        description: formattedFssai,
        mobile: mobileNumber, // Use the verified mobile number directly
        email: restaurantData.email
      };

      const result = await signup(signupData);
      
      // Handle 401 Unauthorized - redirect to login
      if (!result.status && (result.message?.toLowerCase().includes('unauthorized') || result.message?.toLowerCase().includes('login again'))) {
        showError('Session expired', 'Your session has expired. Please login again.');
        setIsLoading(false);
        // Redirect to login page after a short delay
        setTimeout(() => {
          onBack();
        }, 1500);
        return;
      }
      
      if (result.status==true) {
        setSuccess(true);
        showSuccess('Account created', 'Welcome to Menunix!');
        try {
          localStorage.setItem('visitSiteModalPending', '1');
        } catch (_) {}
        
        // Track signup conversion
        // Push event to dataLayer if available (dataLayer is an array)
        if (Array.isArray((window as any).dataLayer)) {
          (window as any).dataLayer.push({
            event: "signup",
          });
        }
        
      } else {
        showError('Signup failed', result.message || 'Please try again.');
      }
    } catch (err) {
      // Check if error is related to 401
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.toLowerCase().includes('unauthorized') || errorMessage.toLowerCase().includes('401')) {
        showError('Session expired', 'Your session has expired. Please login again.');
        setTimeout(() => {
          onBack();
        }, 1500);
        return;
      }
      showError('Error creating account', 'Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center p-3 sm:p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 lg:p-8 text-center">
            <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
              <CheckCircleIcon className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Account Created Successfully!</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              Welcome to your shop dashboard. You can now manage your business efficiently.
            </p>
            <button
              onClick={() => {
                localStorage.setItem('selectedTab', 'products');
                window.location.reload();
              }}
              className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-orange-600 hover:to-pink-700 transition-colors"
            >
              Add Your First Product
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center p-3 sm:p-4">
      <div className="max-w-4xl w-full">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl overflow-hidden">
          {/* Header with Steps */}
          <div className="bg-gradient-to-r from-orange-500 to-pink-600 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center">
                <button
                  onClick={onBack}
                  className="text-white hover:text-orange-200 mr-3 sm:mr-4"
                >
                  <ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
                <div>
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">Create Account</h2>
                  <p className="text-orange-100 mt-1 text-sm sm:text-base">Join our platform and grow your business</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className={`flex items-center ${currentStep >= 1 ? 'text-white' : 'text-orange-200'}`}>
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-1 sm:mr-2 ${
                    currentStep >= 1 ? 'bg-white text-orange-600' : 'bg-orange-500'
                  }`}>
                    {mobileVerified ? <CheckCircleIcon className="h-3 w-3 sm:h-5 sm:w-5" /> : '1'}
                  </div>
                  <span className="text-xs sm:text-sm font-medium">Mobile</span>
                </div>
                <div className={`flex items-center ${currentStep >= 2 ? 'text-white' : 'text-orange-200'}`}>
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-1 sm:mr-2 ${
                    currentStep >= 2 ? 'bg-white text-orange-600' : 'bg-orange-500'
                  }`}>
                    2
                  </div>
                  <span className="text-xs sm:text-sm font-medium">Details</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6 lg:p-8">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 sm:mb-6">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Step 1: Mobile Verification */}
            {currentStep === 1 && (
              <div className="max-w-md mx-auto">
                <div className="text-center mb-6 sm:mb-8">
                  <PhoneIcon className="h-10 w-10 sm:h-12 sm:w-12 text-orange-600 mx-auto mb-3 sm:mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Verify Your Mobile Number</h3>
                  <p className="text-sm sm:text-base text-gray-600 mt-2">We'll send you an OTP to verify your mobile number</p>
                </div>

                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 focus:border-transparent text-base"
                        placeholder="Enter your full name"
                        maxLength={50}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-base ${
                          mobileNumber.length === 10 && /^[6-9]\d{9}$/.test(mobileNumber)
                            ? 'border-green-300 focus:ring-green-500'
                            : mobileNumber.length > 0
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-pink-600'
                        }`}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                      />
                    </div>
                    {mobileNumber.length > 0 && mobileNumber.length < 10 && (
                      <p className="mt-1 text-sm text-red-600">Mobile number must be exactly 10 digits</p>
                    )}
                    {mobileNumber.length === 10 && !/^[6-9]\d{9}$/.test(mobileNumber) && (
                      <p className="mt-1 text-sm text-red-600">Mobile number must start with 6-9</p>
                    )}
                    {mobileNumber.length === 10 && /^[6-9]\d{9}$/.test(mobileNumber) && (
                      <p className="mt-1 text-sm text-green-600">✓ Valid mobile number</p>
                    )}
                  </div>

                  {!isOtpSent ? (
                    <>
                      {renderPolicyAgreement()}
                      <button
                        onClick={sendOtp}
                        disabled={
                          isLoading ||
                          mobileNumber.length !== 10 ||
                          !/^[6-9]\d{9}$/.test(mobileNumber) ||
                          !userName.trim() ||
                          !hasAcceptedPolicies
                        }
                        className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-orange-600 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isLoading ? 'Sending OTP...' : 'Send OTP'}
                      </button>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter OTP *
                        </label>
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 focus:border-transparent text-center text-base sm:text-lg tracking-widest"
                          placeholder="000000"
                          maxLength={6}
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          OTP sent to +91 {mobileNumber}
                        </p>
                      </div>

                      {renderPolicyAgreement()}

                      <div className="flex space-x-3">
                        <button
                          onClick={sendOtp}
                          disabled={isLoading || !hasAcceptedPolicies}
                          className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Resend OTP
                        </button>
                        <button
                          onClick={verifyOtp}
                          disabled={isVerifyingOtp || otp.length !== 6 || !hasAcceptedPolicies}
                          className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                        </button>
                      </div>
                    </div>
                  )}

                  {mobileVerified && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-green-800 font-medium">Mobile number verified successfully!</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Account & Restaurant Details */}
            {currentStep === 2 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {/* Account Details */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                    <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-orange-600" />
                    Account Details
                  </h3>
                  
                  <form className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={userName}
                          readOnly
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed text-base"
                          placeholder="Your name"
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-500">Your name</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mobile Number *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <PhoneIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          value={mobileNumber}
                          readOnly
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed text-base"
                          placeholder="Mobile number"
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-500">Mobile number is verified and cannot be changed</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <LockClosedIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          {...registerStep1('password', {
                            required: 'Password is required',
                            minLength: {
                              value: 8,
                              message: 'Password must be at least 8 characters',
                            },
                            maxLength: {
                              value: 50,
                              message: 'Password must be less than 50 characters',
                            },
                            pattern: {
                              value: /^(?=.*\d)(?=.*[@$!%*?&#]).+$/,
                              message: 'Password must contain at least one number, and one special character',
                            },
                            onChange: () => {
                              // Trigger validation for confirmPassword when password changes
                              triggerStep1('confirmPassword');
                            },
                          })}
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 focus:border-transparent text-base"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      {errorsStep1.password && (
                        <p className="mt-1 text-sm text-red-600">{errorsStep1.password.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <LockClosedIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          {...registerStep1('confirmPassword', {
                            required: 'Please confirm your password',
                            validate: (value) => {
                              const password = watchStep1('password');
                              return value === password || 'Passwords do not match';
                            }
                          })}
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 focus:border-transparent text-base"
                          placeholder="Confirm your password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                      {errorsStep1.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">{errorsStep1.confirmPassword.message}</p>
                      )}
                    </div>
                  </form>
                </div>

                {/* Restaurant Details */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                    <BuildingStorefrontIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-orange-600" />
                    Shop Details
                  </h3>

                  <form onSubmit={handleSubmitStep2(onSubmitStep2)} className="space-y-3 sm:space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Shop Name *
                        </label>
                        <input
                          {...registerStep2('name', { required: 'Shop name is required' })}
                          type="text"
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 text-base"
                          placeholder="Enter shop name"
                        />
                        {errorsStep2.name && (
                          <p className="mt-1 text-sm text-red-600">{errorsStep2.name.message}</p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Shop Username *
                        </label>
                        <input
                          {...shopUsernameField}
                          value={shopUsername || ''}
                          onChange={(event) => {
                            const normalizedValue = event.target.value
                              .toLowerCase()
                              .trim();
                            setValue('id', normalizedValue, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                          type="text"
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 text-base"
                          placeholder="Enter shop username"
                        />
                        {errorsStep2.id && (
                          <p className="mt-1 text-sm text-red-600">{errorsStep2.id.message}</p>
                        )}
                        <p className="mt-1 text-sm text-gray-500">Enter a unique shop username. It will be automatically converted to lowercase.</p>
                        <p className="mt-2 text-sm text-gray-600">
                          {shopPreviewUrl
                            ? (
                              <>
                                Your public store link will be{' '}
                                <span className="font-semibold text-gray-900 break-all">{shopPreviewUrl}</span>
                              </>
                              )
                            : 'Your public store link appears here once you enter a valid shop username.'}
                        </p>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          FSSAI License Number *
                        </label>
                        <input
                          {...registerStep2('description', { 
                            required: 'FSSAI License Number is required',
                            pattern: {
                              value: /^\d{14}$/,
                              message: 'Enter a valid 14-digit FSSAI number'
                            }
                          })}
                          type="text"
                          maxLength={14}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 text-base"
                          placeholder="Enter 14-digit FSSAI License Number"
                        />
                        {errorsStep2.description && (
                          <p className="mt-1 text-sm text-red-600">{errorsStep2.description.message}</p>
                        )}
                        <small className="text-gray-500">Enter 14-digit FSSAI License Number.</small>
                      </div>


                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <PhoneIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            {...registerStep2('phone', { required: 'Phone number is required' })}
                            type="tel"
                            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed text-base"
                            placeholder="Phone number"
                            value={`+91 ${mobileNumber}`}
                            readOnly
                          />
                        </div>
                        <p className="mt-1 text-sm text-gray-500">Phone number is verified and cannot be changed</p>
                        {errorsStep2.phone && (
                          <p className="mt-1 text-sm text-red-600">{errorsStep2.phone.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email (Optional)
                        </label>
                        <input
                          {...registerStep2('email', { 
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Please enter a valid email address',
                            },
                          })}
                          type="email"
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 text-base"
                          placeholder="e.g., info@bellavista.com"
                        />
                        {errorsStep2.email && (
                          <p className="mt-1 text-sm text-red-600">{errorsStep2.email.message}</p>
                        )}
                        <p className="mt-1 text-sm text-gray-500">
                          This email will be shared with Razorpay for receipts and other payment notifications.
                        </p>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address *
                        </label>
                        <textarea
                          {...registerStep2('address', { required: 'Address is required' })}
                          rows={2}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-600 text-base"
                          placeholder="e.g., 123 Main Street, New York, NY 10001"
                        />
                        {errorsStep2.address && (
                          <p className="mt-1 text-sm text-red-600">{errorsStep2.address.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="pt-6">
                      <button
                        type="submit"
                        disabled={isLoading || !hasAcceptedPolicies}
                        className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-orange-600 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isLoading ? 'Creating Account...' : 'Create Account'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;